import React from 'react';
import { useData } from '../EpcrDataContext';
import { TextField, OptionField, MultiOptionField, DateTimeField, NumericField, Section, ViewLayout } from '../EpcrFields';
import { Icons } from '../EpcrIcons';

const SECTION_ORDER = [
  'eRecord', 'eResponse', 'eDispatch', 'eCrew', 'eTimes',
  'ePatient', 'eScene', 'eSituation', 'eHistory',
  'eExam', 'eVitals', 'eLabs', 'eProtocols',
  'eMedications', 'eProcedures', 'eAirway', 'eDevice',
  'eArrest', 'eNarrative', 'eInjury', 'eOutcome',
  'eDisposition', 'eOther', 'ePayment',
  'eCustomConfiguration', 'eCustomResults',
];

const SECTION_TITLES = {
  eRecord: 'Record',            eResponse: 'Response',
  eDispatch: 'Dispatch',        eCrew: 'Crew',
  eTimes: 'Times',              ePatient: 'Patient',
  eScene: 'Scene',              eSituation: 'Situation / Complaint',
  eHistory: 'History',          eExam: 'Physical Exam',
  eVitals: 'Vitals',            eLabs: 'Labs',
  eProtocols: 'Protocols',      eMedications: 'Medications',
  eProcedures: 'Procedures',    eAirway: 'Airway',
  eDevice: 'Device',            eArrest: 'Arrest',
  eNarrative: 'Narrative',      eInjury: 'Injury',
  eOutcome: 'Outcome',          eDisposition: 'Disposition',
  eOther: 'Other',              ePayment: 'Payment',
  eCustomConfiguration: 'Custom Configuration',
  eCustomResults: 'Custom Results',
};


// ── Helpers ──────────────────────────────────────────────────────────────────

// Navigate a slash-path into the report's PatientCareReport node.
function readPath(report, path) {
  try {
    let obj = report.EMSDataSet.Header.PatientCareReport;
    for (const seg of path.split('/')) {
      if (obj == null) return undefined;
      obj = obj[seg];
    }
    return obj;
  } catch { return undefined; }
}

// Given the abstract path for an element inside a repeatable group, insert the
// instance index immediately after the top-level group name segment so that the
// field component reads/writes the correct array slot.
//
// e.g. instancePath("eMedications/eMedications.MedicationGroup/eMedications.03",
//                    "eMedications.MedicationGroup", 0)
//      → "eMedications/eMedications.MedicationGroup/0/eMedications.03"
//
// Works for nested sub-groups too:
//      "eMedications/eMedications.MedicationGroup/eMedications.DosageGroup/eMedications.05"
//      → "eMedications/eMedications.MedicationGroup/0/eMedications.DosageGroup/eMedications.05"
function instancePath(abstractPath, topGroupNumber, index) {
  const marker = `/${topGroupNumber}/`;
  const pos = abstractPath.indexOf(marker);
  if (pos === -1) return abstractPath;
  return (
    abstractPath.slice(0, pos + marker.length) +
    `${index}/` +
    abstractPath.slice(pos + marker.length)
  );
}

// Convert "eMedications.MedicationGroup" → "Medication"
export function groupLabel(groupNumber) {
  const name = groupNumber.split('.').pop();       // "MedicationGroup"
  return name.replace(/Group$/, '').replace(/([A-Z])/g, ' $1').trim();
}

// Normalize a group value from the report to always be an array.
// NEMSIS JSON stores a single instance as a plain object, multiple as an array.
function toArray(val) {
  if (val == null) return [];
  return Array.isArray(val) ? val : [val];
}

// ── NV / PN helpers (Raw-view only) ─────────────────────────────────────────

// If the stored value is an NV or PN code declared in spec, return { label, kind }.
function matchNvPn(value, spec) {
  if (value == null || value === '' || !spec) return null;
  const s = String(value);
  const nv = (spec.nv || []).find(x => x.code === s);
  if (nv) return { label: nv.label, kind: 'nv' };
  const pn = (spec.pn || []).find(x => x.code === s);
  if (pn) return { label: pn.label, kind: 'pn' };
  return null;
}

// "–" button rendered as the suffix on text/numeric/date fields.
// Opens an inline dropdown listing all NV and PN codes for the element.
// Clicking an already-active code clears it (toggle).
function RawNvBtn({ spec, value, path }) {
  const data = useData();
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);

  const codes = [
    ...(spec?.nv || []).map(x => ({ ...x, kind: 'nv' })),
    ...(spec?.pn || []).map(x => ({ ...x, kind: 'pn' })),
  ];

  React.useEffect(() => {
    if (!open) return;
    const handler = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSelect = code => {
    data.setReportPath(path, String(value) === String(code) ? '' : code);
    setOpen(false);
  };

  const isActive = matchNvPn(value, spec) !== null;

  return (
    <div className="raw-nv-btn-wrap" ref={wrapRef}>
      <button
        className={'raw-nv-btn' + (isActive ? ' active' : '')}
        onClick={() => setOpen(v => !v)}
        title="Not values / Pertinent negatives"
      >
        –
      </button>
      {open && (
        <div className="raw-nv-dropdown">
          {codes.map(x => (
            <button
              key={x.code}
              className={'raw-nv-opt ' + x.kind + (String(value) === x.code ? ' active' : '')}
              onClick={() => handleSelect(x.code)}
            >
              <span className="raw-nv-code">{x.code}</span>
              {x.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Rendered in place of TextField/NumericField/DateTimeField when the stored
// value is a NV or PN code — shows a labelled badge instead of the input.
function RawNvBadgeField({ label, resolvedElem, spec, nvMatch, value, path }) {
  const data = useData();
  const req  = spec?.usage === 'Mandatory' || spec?.usage === 'Required';
  const nvCodes = [...(spec?.nv || []), ...(spec?.pn || [])];
  return (
    <div className="field" id={resolvedElem ? `field-${resolvedElem}` : undefined}>
      <label className="field-label">
        <span>{label}</span>
        {req && <span className="req">*</span>}
        {resolvedElem && <span className="nem">{resolvedElem}</span>}
      </label>
      <div className="field-input-row">
        <div className={'nv-badge nv-badge-' + nvMatch.kind}>
          <span className="nv-badge-label">{nvMatch.label}</span>
          <button
            className="nv-badge-clear"
            onClick={() => data.setReportPath(path, '')}
            title="Clear — enter a real value"
          >
            <Icons.close size={12} />
          </button>
        </div>
        {nvCodes.length > 0 && <RawNvBtn spec={spec} value={value} path={path} />}
      </div>
    </div>
  );
}

// ── Field renderer ────────────────────────────────────────────────────────────
// Accepts either elem (for flat fields) or path (for group-instance fields).
// When only path is given, the field components derive elem from the last path segment.
// For text / numeric / date fields, injects a NV button as a suffix and replaces
// the input with a badge when a NV/PN code is currently stored.
export function RawField({ elem, path: explicitPath }) {
  const data = useData();
  const resolvedElem = elem || (explicitPath ? explicitPath.split('/').pop() : null);
  const spec = resolvedElem ? data.spec(resolvedElem) : null;
  if (!spec) return null;

  const label = spec.name || resolvedElem;
  const bt    = spec.baseType;
  // Always pass elem so field components can resolve spec/options/labels.
  // When explicitPath is set (group-instance path with numeric index), pass
  // it as path so reads/writes target the correct array slot instead of the
  // abstract path that data.path(elem) would return.
  const props = explicitPath
    ? { label, elem: resolvedElem, path: explicitPath }
    : { label, elem: resolvedElem };

  // Option fields (OptionField / MultiOptionField) already expose NV/PN codes
  // through the PickerModal's special section — no extra handling needed.
  if (bt !== 'dateTime' && bt !== 'date' && bt !== 'base64Binary') {
    const opts = data.options(resolvedElem);
    if (opts.length > 0) {
      if (spec?.maxOccurs === 'M') return <MultiOptionField {...props} />;
      return <OptionField {...props} />;
    }
  }

  if (bt === 'base64Binary') return null;

  // For text / numeric / date fields: resolve the concrete path, read the
  // current value, and detect NV/PN codes.
  const resolvedPath = explicitPath || data.path(resolvedElem);
  const currentValue = resolvedPath ? readPath(data.report, resolvedPath) : null;
  const nvMatch      = matchNvPn(currentValue, spec);
  const nvCodes      = [...(spec?.nv || []), ...(spec?.pn || [])];
  const nvBtn = (resolvedPath && nvCodes.length > 0)
    ? <RawNvBtn spec={spec} value={currentValue} path={resolvedPath} />
    : null;

  // When an NV/PN code is stored, show a badge instead of the broken input.
  if (nvMatch && resolvedPath) {
    return (
      <RawNvBadgeField
        label={label}
        resolvedElem={resolvedElem}
        spec={spec}
        nvMatch={nvMatch}
        value={currentValue}
        path={resolvedPath}
      />
    );
  }

  if (bt === 'dateTime' || bt === 'date') return <DateTimeField {...props} suffix={nvBtn} />;
  if (bt === 'integer' || bt === 'decimal' || bt === 'positiveInteger') return <NumericField {...props} suffix={nvBtn} />;
  return <TextField {...props} suffix={nvBtn} />;
}

// ── Singular group (maxOccurs="1") ───────────────────────────────────────────
// Renders as a labelled sub-section; uses abstract paths (data.path works fine).
function SingularGroup({ groupNumber, elems }) {
  const label = groupLabel(groupNumber);
  if (elems.length === 0) return null;
  return (
    <div className="raw-group-block">
      <div className="raw-group-label">{label}</div>
      <div className="fgrid">
        {elems.map(elem => (
          <div key={elem} className="fcol-12">
            <RawField elem={elem} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Repeatable group (maxOccurs="M") ─────────────────────────────────────────
// Reads the group array from the report, renders one card per instance,
// and provides Add / Remove controls.
function RepeatableGroup({ groupSpec, elems }) {
  const data        = useData();
  const groupNumber = groupSpec.path.split('/').pop();
  const label       = groupLabel(groupNumber);
  const instances   = toArray(readPath(data.report, groupSpec.path));

  const handleAdd = () => {
    data.setReportPath(groupSpec.path, [...instances, {}]);
  };

  const handleRemove = (index) => {
    const next = instances.filter((_, i) => i !== index);
    data.setReportPath(groupSpec.path, next);
  };

  return (
    <div className="raw-group-block raw-group-repeatable">
      <div className="raw-group-label raw-group-label-repeat">
        {label}
        <span className="raw-group-count">{instances.length} instance{instances.length !== 1 ? 's' : ''}</span>
      </div>

      {instances.length === 0 && (
        <div className="raw-group-empty">No {label.toLowerCase()} recorded.</div>
      )}

      {instances.map((_, index) => (
        <GroupInstanceCard
          key={index}
          index={index}
          label={label}
          groupNumber={groupNumber}
          elems={elems}
          onRemove={() => handleRemove(index)}
          canRemove={instances.length > 1}
        />
      ))}

      <button className="raw-group-add" onClick={handleAdd}>
        + Add {label}
      </button>
    </div>
  );
}

// One card for a single group instance.
function GroupInstanceCard({ index, label, groupNumber, elems, onRemove, canRemove }) {
  const data = useData();
  return (
    <div className="raw-instance-card">
      <div className="raw-instance-head">
        <span className="raw-instance-label">{label} #{index + 1}</span>
        {canRemove && (
          <button className="raw-instance-remove" onClick={onRemove} title={`Remove ${label} #${index + 1}`}>
            ×
          </button>
        )}
      </div>
      <div className="fgrid">
        {elems.map(elem => {
          const abstract = data.path(elem);
          if (!abstract) return null;
          const path = instancePath(abstract, groupNumber, index);
          return (
            <div key={elem} className="fcol-12">
              <RawField path={path} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Section renderer ──────────────────────────────────────────────────────────
function SectionView({ prefix, elems, sectionIndex }) {
  const data = useData();

  // Top-level groups for this section (no parentGroup = owned directly by section).
  const topGroups = React.useMemo(() => {
    return Object.entries(data.groups || {})
      .filter(([, g]) => g.section === prefix && !g.parentGroup)
      .map(([number, g]) => ({ number, ...g }));
  }, [data.groups, prefix]);

  // Flat elements: not under any top-level group.
  const flatElems = React.useMemo(() => {
    if (topGroups.length === 0) return elems;
    return elems.filter(elem => {
      const p = data.path(elem);
      return !p || !topGroups.some(g => p.startsWith(g.path + '/'));
    });
  }, [elems, topGroups, data]);

  // Elements belonging to each top-level group (includes nested sub-group elements).
  const elemsForGroup = React.useCallback((groupPath) => {
    return elems.filter(elem => {
      const p = data.path(elem);
      return p && p.startsWith(groupPath + '/');
    });
  }, [elems, data]);

  return (
    <Section
      id={`raw-${prefix}`}
      num={String(sectionIndex + 1).padStart(2, '0')}
      title={SECTION_TITLES[prefix] || prefix}
      meta={prefix}
    >
      {flatElems.length > 0 && (
        <div className="fgrid">
          {flatElems.map(elem => (
            <div key={elem} className="fcol-12">
              <RawField elem={elem} />
            </div>
          ))}
        </div>
      )}

      {topGroups.map(g => {
        const groupElems = elemsForGroup(g.path);
        return g.maxOccurs === 'M'
          ? <RepeatableGroup key={g.number} groupSpec={g} elems={groupElems} />
          : <SingularGroup   key={g.number} groupNumber={g.number} elems={groupElems} />;
      })}
    </Section>
  );
}

// ── Main view ─────────────────────────────────────────────────────────────────
export function EpcrViewRaw() {
  const data = useData();

  const grouped = React.useMemo(() => {
    const out = {};
    for (const elem of Object.keys(data.paths || {})) {
      const prefix = elem.split('.')[0];
      if (!out[prefix]) out[prefix] = [];
      out[prefix].push(elem);
    }
    return out;
  }, [data.paths]);

  const activeSections = SECTION_ORDER.filter(p => grouped[p]?.length > 0);

  const sections = activeSections.map((prefix, i) => ({
    id:    `raw-${prefix}`,
    label: SECTION_TITLES[prefix] || prefix,
  }));

  return (
    <ViewLayout sections={sections}>
      {activeSections.map((prefix, i) => (
        <SectionView
          key={prefix}
          prefix={prefix}
          elems={grouped[prefix]}
          sectionIndex={i}
        />
      ))}
    </ViewLayout>
  );
}
